import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { financeDashboardKeys } from "@/features/finance/hooks/query-keys";
import { purchaseOrderKeys } from "@/features/purchase-orders/hooks/query-keys";
import { vendorCreditKeys } from "@/features/vendor-credits/hooks/query-keys";
import {
  applyVendorCredit,
  getPurchaseOrdersForCreditIssuance,
  getPurchaseOrdersForVendorCreditApplication,
  getVendorAccountsForCreditIssuance,
  getVendorCreditApplications,
  getVendorCreditBalances,
  getVendorCreditById,
  getVendorCredits,
  issueVendorCredit,
  reverseVendorCreditApplication,
} from "@/features/vendor-credits/hooks/vendor-credit-client";
import type {
  ApplyVendorCreditPayload,
  IssueVendorCreditPayload,
  ReverseVendorCreditApplicationPayload,
} from "@/features/vendor-credits/types";

/** Fetches one vendor credit detail record when a selected credit id is present. */
export function useVendorCredit(creditId: string | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: creditId ? vendorCreditKeys.detail(creditId) : vendorCreditKeys.all,
    queryFn: () => getVendorCreditById(creditId as string),
    enabled: Boolean(creditId) && (options?.enabled ?? true),
  });
}

/** Fetches aggregate vendor credit balances grouped by vendor account. */
export function useVendorCreditBalances() {
  return useQuery({
    queryKey: vendorCreditKeys.balances(),
    queryFn: () => getVendorCreditBalances(),
  });
}

/** Fetches the recent issued vendor credits list for the credit management page. */
export function useVendorCredits(limit = 200) {
  return useQuery({
    queryKey: vendorCreditKeys.list({ limit }),
    queryFn: () => getVendorCredits({ limit }),
  });
}

/** Fetches the recent vendor-credit applications list for reversal workflows. */
export function useVendorCreditApplications(limit = 200) {
  return useQuery({
    queryKey: vendorCreditKeys.applicationList({ limit }),
    queryFn: () => getVendorCreditApplications({ limit }),
  });
}

const PURCHASE_ORDER_FETCHERS_BY_SCOPE = {
  "vendor-credit-application": getPurchaseOrdersForVendorCreditApplication,
  "vendor-credit-issuance": getPurchaseOrdersForCreditIssuance,
} as const;

/** Fetches scoped purchase orders for either vendor-credit application or issuance. */
export function usePurchaseOrdersForVendorCredit(
  scope: keyof typeof PURCHASE_ORDER_FETCHERS_BY_SCOPE,
  enabled: boolean,
  limit = 50,
  accountId?: string,
) {
  return useQuery({
    enabled: enabled && Boolean(accountId),
    queryKey: purchaseOrderKeys.list({
      scope,
      limit,
      accountId: accountId ?? null,
    }),
    queryFn: () =>
      PURCHASE_ORDER_FETCHERS_BY_SCOPE[scope]({
        account_id: accountId,
        limit,
      }),
  });
}

/** Fetches vendor accounts available for issuing a manual vendor credit. */
export function useVendorAccountsForCreditIssuance(enabled: boolean, search?: string) {
  return useQuery({
    enabled,
    queryKey: [...vendorCreditKeys.all, "issuance-accounts", search ?? ""],
    queryFn: () => getVendorAccountsForCreditIssuance({ search }),
  });
}

function useInvalidateVendorCreditQueries() {
  const queryClient = useQueryClient();
  return (purchaseOrderId?: string) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: vendorCreditKeys.all }),
      ...(purchaseOrderId
        ? [
            queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all }),
            queryClient.invalidateQueries({
              queryKey: purchaseOrderKeys.detail(purchaseOrderId),
            }),
            // Settling against a purchase order moves its balance, which is the
            // finance dashboard's payables aging and total.
            queryClient.invalidateQueries({ queryKey: financeDashboardKeys.all }),
          ]
        : []),
    ]);
}

/** Applies a vendor credit and invalidates affected credit and purchase-order caches. */
export function useApplyVendorCredit() {
  const invalidate = useInvalidateVendorCreditQueries();

  return useMutation({
    mutationFn: (payload: ApplyVendorCreditPayload) => applyVendorCredit(payload),
    onSuccess: (_result, payload) => invalidate(payload.purchase_order_id),
  });
}

/** Reverses a vendor-credit application and refreshes affected cache scopes. */
export function useReverseVendorCreditApplication() {
  const invalidate = useInvalidateVendorCreditQueries();

  return useMutation({
    mutationFn: (payload: ReverseVendorCreditApplicationPayload) =>
      reverseVendorCreditApplication(payload),
    onSuccess: (_result, payload) => invalidate(payload.purchase_order_id),
  });
}

/** Issues a vendor credit and refreshes the vendor-credit query family. */
export function useIssueVendorCredit() {
  const invalidate = useInvalidateVendorCreditQueries();

  return useMutation({
    mutationFn: (payload: IssueVendorCreditPayload) => issueVendorCredit(payload),
    onSuccess: () => invalidate(),
  });
}
